import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, ThumbsUp, PlusCircle, ShieldCheck, Filter, Wrench, Search } from "lucide-react";

const SITE = "https://spa-report-hub.emergent.host";

export default function Tutorials() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";
  const activeBrand = searchParams.get("brand") || "";
  const activeTag = searchParams.get("tag") || "";
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const activeSearch = searchParams.get("search") || "";

  useSEO({
    title: "Tutos communautaires — Réparez votre spa avec la communauté | DoctoSpa",
    description:
      "Tutoriels pratiques partagés par les propriétaires de spas et les techniciens vérifiés : réparation, entretien, remplacement de pièces. Guides testés et validés par la communauté.",
    url: `${SITE}/tutos`,
    type: "website",
  });

  useEffect(() => {
    api.get("/tutorials/categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (activeBrand) params.brand = activeBrand;
    if (activeTag) params.tag = activeTag;
    if (activeSearch) params.search = activeSearch;
    api
      .get("/tutorials", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [activeCategory, activeBrand, activeTag, activeSearch]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setParam("search", searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams({});
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="tutorials-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <div className="eyebrow text-secondary">Communauté</div>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            Tutos <span className="text-primary">testés</span> par la communauté.
          </h1>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Guides pratiques rédigés par les propriétaires et techniciens spa. Partagez vos astuces,
            aidez les autres — un badge <span className="inline-flex items-center gap-1 text-secondary font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> Vérifié</span> distingue les techniciens.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {user ? (
            <Link to="/tutos/nouveau" data-testid="btn-create-tutorial">
              <Button className="rounded-full h-11 px-6" size="lg">
                <PlusCircle className="h-4 w-4 mr-2" />
                Proposer un tuto
              </Button>
            </Link>
          ) : (
            <Link to="/login" data-testid="btn-login-to-create">
              <Button variant="outline" className="rounded-full h-11 px-6">
                Se connecter pour publier
              </Button>
            </Link>
          )}
          {user && (
            <Link to="/tutos/mes-tutos" className="text-xs text-muted-foreground hover:text-primary text-right" data-testid="link-my-tutos">
              → Mes tutos
            </Link>
          )}
        </div>
      </div>

      {/* Search + filters */}
      <div className="mt-10 grid gap-4">
        <form onSubmit={onSearchSubmit} className="flex gap-2 max-w-xl" data-testid="tuto-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Rechercher un tuto (ex. pressostat Balboa)"
              className="pl-9"
              data-testid="tuto-search-input"
            />
          </div>
          <Button type="submit" data-testid="tuto-search-submit">Rechercher</Button>
        </form>

        <div className="flex flex-wrap gap-2" data-testid="tuto-category-filter">
          <button
            onClick={() => setParam("category", "")}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              !activeCategory ? "bg-secondary text-white" : "bg-black/5 hover:bg-black/10 text-foreground"
            }`}
            data-testid="tuto-filter-all"
          >
            Toutes catégories
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setParam("category", c.slug)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                activeCategory === c.slug ? "bg-secondary text-white" : "bg-black/5 hover:bg-black/10 text-foreground"
              }`}
              data-testid={`tuto-filter-${c.slug}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {(activeBrand || activeTag || activeSearch) && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Filtres actifs :</span>
            {activeBrand && (
              <Badge variant="outline" className="rounded-full cursor-pointer" onClick={() => setParam("brand", "")} data-testid="filter-brand-chip">
                Marque : {activeBrand} ✕
              </Badge>
            )}
            {activeTag && (
              <Badge variant="outline" className="rounded-full cursor-pointer" onClick={() => setParam("tag", "")} data-testid="filter-tag-chip">
                Tag : {activeTag} ✕
              </Badge>
            )}
            {activeSearch && (
              <Badge variant="outline" className="rounded-full cursor-pointer" onClick={() => { setSearchInput(""); setParam("search", ""); }} data-testid="filter-search-chip">
                Recherche : "{activeSearch}" ✕
              </Badge>
            )}
            <button className="text-primary hover:underline text-xs ml-1" onClick={clearFilters} data-testid="tuto-clear-filters">
              tout effacer
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <p className="mt-16 text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <Card className="mt-12 p-10 border-dashed text-center" data-testid="tuto-empty-state">
          <BookOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-display text-xl font-bold">Aucun tuto trouvé.</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Soyez le premier à partager votre expérience avec la communauté !
          </p>
          {user ? (
            <Link to="/tutos/nouveau">
              <Button className="mt-4 rounded-full">Proposer mon premier tuto</Button>
            </Link>
          ) : (
            <Link to="/register">
              <Button className="mt-4 rounded-full">Créer un compte</Button>
            </Link>
          )}
        </Card>
      ) : (
        <>
          <p className="mt-10 text-xs text-muted-foreground">
            {total} tuto{total > 1 ? "s" : ""} publié{total > 1 ? "s" : ""}
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tuto-grid">
            {items.map((t) => (
              <Link
                key={t.id}
                to={`/tutos/${t.slug}`}
                className="group"
                data-testid={`tuto-card-${t.slug}`}
              >
                <Card className="h-full border-black/10 overflow-hidden hover:border-primary/40 hover:shadow-lg transition-all flex flex-col">
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 grid place-items-center overflow-hidden">
                    {t.cover_photo_url ? (
                      <img
                        src={`${backend}${t.cover_photo_url}`}
                        alt={t.title}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <Wrench className="h-10 w-10 text-white/60" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="secondary" className="rounded-full capitalize">{t.category.replace("-", " ")}</Badge>
                      {t.spa_brand && (
                        <Badge variant="outline" className="rounded-full">{t.spa_brand}</Badge>
                      )}
                    </div>
                    <h3 className="mt-3 font-display font-bold text-lg leading-tight group-hover:text-primary transition-colors line-clamp-2">
                      {t.title}
                    </h3>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                      {t.summary}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="font-medium text-foreground">{t.author_name}</span>
                        {t.author_is_technician && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-secondary/10 text-secondary font-semibold text-[10px]" data-testid="verified-badge">
                            <ShieldCheck className="h-2.5 w-2.5" /> Vérifié
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1 text-primary font-semibold">
                        <ThumbsUp className="h-3.5 w-3.5" /> {t.helpful_count}
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
